import { z } from "zod/v4";

/**
 * Barcode normalization boundary shared by import and lookup paths.
 *
 * Canonicalization is digits-only and rejects restricted/private-label ranges
 * so downstream adapters can treat failures as expected parse outcomes.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace Barcode {
  export type Canonical = string & { readonly __brand: "Barcode.Canonical" };

  export type ParseFailure = "empty" | "non_digits" | "restricted";

  const digitsOnlyRegex = /^\d+$/;

  export function parse(
    input: string,
  ): { ok: true; code: Canonical } | { ok: false; reason: ParseFailure } {
    const trimmed = input.trim();
    if (trimmed.length === 0) {
      return { ok: false, reason: "empty" };
    }

    if (!digitsOnlyRegex.test(trimmed)) {
      return { ok: false, reason: "non_digits" };
    }

    const code = canonicalizeDigits(trimmed);
    if (!code) {
      return { ok: false, reason: "restricted" };
    }

    return { ok: true, code };
  }

  export const schema = z.string().transform((value) => {
    const result = parse(value);
    if (result.ok) {
      return result.code;
    }

    return z.NEVER;
  });

  export function canonicalizeDigits(digits: string): Canonical | null {
    if (!digitsOnlyRegex.test(digits)) {
      return null;
    }

    const withoutLeadingZeros = digits.replace(/^0+/, "");
    const significantDigitsLength = withoutLeadingZeros.length;

    let normalized = digits;
    if (significantDigitsLength <= 7) {
      normalized = withoutLeadingZeros.padStart(8, "0");
    } else if (significantDigitsLength >= 9 && significantDigitsLength <= 12) {
      normalized = withoutLeadingZeros.padStart(13, "0");
    }

    const canonical = normalized as Canonical;
    if (isRestricted(canonical)) {
      return null;
    }

    return canonical;
  }

  export function isRestricted(code: Canonical): boolean {
    const raw = code as string;

    if (/^0{7}[0-9]{5,6}$/.test(raw)) {
      return true;
    }

    if (/^04[0-9]{10,11}$/.test(raw)) {
      return true;
    }

    if (/^[02][0-9]{2}[0-9]{5}$/.test(raw)) {
      return true;
    }

    if (raw.startsWith("02") && raw.length >= 3) {
      const thirdDigit = Number.parseInt(raw.charAt(2), 10);
      if (!Number.isNaN(thirdDigit) && thirdDigit >= 0 && thirdDigit <= 9) {
        return true;
      }
    }

    if (raw.startsWith("2") && raw.length >= 3) {
      const secondChar = raw.charAt(1);
      const thirdChar = raw.charAt(2);
      if (secondChar >= "0" && secondChar <= "9" && thirdChar >= "0" && thirdChar <= "9") {
        return true;
      }
    }

    return false;
  }
}
